import React from 'react';

const DEFAULT_NOTIFICATION_SETTINGS = {
  bookingConfirmation: true,
  bookingSmsConfirmation: true,
  completionReminder: true,
  preCourseInfo: true,
  attendanceLink: true,
  diplomaSurvey: true,
  surveyReminder: true
};

function CourseTemplateTab({ courses, onEdit, onDelete, onNew }) {
  return (
    <>
      <header className="courses-header">
        <div>
          <h1>Kursmallar</h1>
          <p>Hantera kurstyper och standardinställningar</p>
        </div>
        <button className="btn btn-primary" onClick={onNew}>
          + Ny kursmall
        </button>
      </header>

      <div className="courses-table-container">
        <table className="courses-table">
          <thead>
            <tr>
              <th>Kursnamn</th>
              <th>Beskrivning</th>
              <th>Pris</th>
              <th>Dagar</th>
              <th>Max</th>
              <th>Status</th>
              <th>Åtgärder</th>
            </tr>
          </thead>
          <tbody>
            {courses.map(course => (
              <tr key={course.id}>
                <td className="course-name-cell">{course.name}</td>
                <td className="course-desc-cell">{course.description || '-'}</td>
                <td>{course.price} kr</td>
                <td>{course.durationDays}</td>
                <td>{course.maxParticipants}</td>
                <td>
                  <span className={`status-badge ${course.isActive ? 'active' : 'inactive'}`}>
                    {course.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td>
                  <div className="table-actions">
                    <button className="btn btn-secondary btn-small" onClick={() => onEdit(course)}>
                      Redigera
                    </button>
                    <button className="btn btn-danger btn-small" onClick={() => onDelete(course)}>
                      Ta bort
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {courses.length === 0 && (
          <div className="no-data">
            <p>Inga kursmallar registrerade.</p>
            <p>Klicka på "+ Ny kursmall" för att lägga till en.</p>
          </div>
        )}
      </div>
    </>
  );
}

function CourseModal({ show, editing, form, setForm, onSubmit, onClose }) {
  if (!show) return null;

  const NOTIFICATION_TYPES = [
    { key: 'bookingConfirmation', label: 'Bokningsbekräftelse (mejl)' },
    { key: 'bookingSmsConfirmation', label: 'Bokningsbekräftelse (SMS)' },
    { key: 'completionReminder', label: 'Kompletteringspåminnelser' },
    { key: 'preCourseInfo', label: 'Förhandsinformation (14 dagar)' },
    { key: 'attendanceLink', label: 'Närvarolänk till lärare' },
    { key: 'diplomaSurvey', label: 'Diplom, LinkedIn & enkät' },
    { key: 'surveyReminder', label: 'Enkätpåminnelser' },
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <h2>{editing ? 'Redigera kursmall' : 'Skapa ny kursmall'}</h2>
        <form onSubmit={onSubmit}>
          <div className="form-group">
            <label>Kursnamn *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              required
              placeholder="T.ex. AI för projektledare"
            />
          </div>
          <div className="form-group">
            <label>Beskrivning</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows="3"
              placeholder="Beskriv kursen..."
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Standardpris (kr) *</label>
              <input
                type="number"
                value={form.price}
                onChange={(e) => setForm({...form, price: e.target.value})}
                placeholder="4995"
                required
              />
            </div>
            <div className="form-group">
              <label>Antal dagar</label>
              <input
                type="number"
                value={form.durationDays}
                onChange={(e) => setForm({...form, durationDays: e.target.value})}
                min="1"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Max deltagare</label>
              <input
                type="number"
                value={form.maxParticipants}
                onChange={(e) => setForm({...form, maxParticipants: e.target.value})}
                min="1"
              />
            </div>
            <div className="form-group">
              <label>Min deltagare</label>
              <input
                type="number"
                value={form.minParticipants}
                onChange={(e) => setForm({...form, minParticipants: e.target.value})}
                min="1"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Förkunskapskrav</label>
            <textarea
              value={form.prerequisites}
              onChange={(e) => setForm({...form, prerequisites: e.target.value})}
              rows="2"
              placeholder="T.ex. Grundläggande datorkunskap..."
            />
          </div>
          <div className="form-group">
            <label>Lärandemål</label>
            <textarea
              value={form.learningObjectives}
              onChange={(e) => setForm({...form, learningObjectives: e.target.value})}
              rows="3"
              placeholder="Efter kursen kommer deltagaren att kunna..."
            />
          </div>
          <div className="form-group">
            <label>Info-sida URL</label>
            <input
              type="url"
              value={form.infoPageUrl}
              onChange={(e) => setForm({...form, infoPageUrl: e.target.value})}
              placeholder="https://projektledarpodden.se/kurser/..."
            />
          </div>
          <div className="form-group">
            <label>Utvärderingsformulär URL</label>
            <input
              type="url"
              value={form.surveyFormUrl}
              onChange={(e) => setForm({...form, surveyFormUrl: e.target.value})}
              placeholder="https://forms.microsoft.com/..."
            />
          </div>

          <div className="notification-settings-section">
            <h4>Aviseringsinställningar (standard för alla tillfällen)</h4>
            <div className="notification-settings-grid">
              {NOTIFICATION_TYPES.map(({ key, label }) => (
                <label key={key} className="notification-checkbox">
                  <input
                    type="checkbox"
                    checked={form.notificationSettings?.[key] !== false}
                    onChange={(e) => setForm({
                      ...form,
                      notificationSettings: { ...form.notificationSettings, [key]: e.target.checked }
                    })}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({...form, isActive: e.target.checked})}
              />
              Aktiv (visas i kurslistor och kan användas för nya kurstillfällen)
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-success">
              {editing ? 'Spara ändringar' : 'Skapa kurs'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Avbryt
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export { CourseTemplateTab, CourseModal, DEFAULT_NOTIFICATION_SETTINGS };
